import { isWeekend } from 'date-fns';

import { TimeOffRequestSummaryStatus, TimeOffSummaryItem } from '../../../types/time-tracking';
import { WeekSlot } from '../week-slot';

/**
 * Pure derivation helpers + layout constants shared by the time-off strip
 * cells. Kept free of JSX so the cell components stay focused on rendering.
 */

/**
 * Fraction of a day cell a segment must occupy to fit the type + hours on two
 * lines. Below it we show a single "Type · Xh" line; far below it (too thin to
 * read at all) we render nothing and rely on the hover tooltip.
 */
export const TWO_LINE_HEIGHT_FRACTION = 0.5;
export const MIN_VISIBLE_HEIGHT_FRACTION = 0.18;

export interface TimeOffTotals {
  approved: number;
  requested: number;
}

/** Weekday time-off entries for a slot; weekends are always empty (no time off allowed). */
export const entriesForSlot = (slot: WeekSlot): TimeOffSummaryItem[] =>
  isWeekend(slot.date) ? [] : (slot.breakdown?.timeOff ?? []);

/** Sums weekday time off only — weekend time off is never counted. */
export const sumTimeOffTotals = (slots: WeekSlot[]): TimeOffTotals =>
  slots.reduce<TimeOffTotals>(
    (totals, slot) => {
      for (const entry of entriesForSlot(slot)) {
        if (entry.status === TimeOffRequestSummaryStatus.APPROVED) {
          totals.approved += entry.totalHours;
        } else {
          totals.requested += entry.totalHours;
        }
      }
      return totals;
    },
    { approved: 0, requested: 0 }
  );

/** Taller cells when any weekday stacks 2+ entries, so segments keep their labels. */
export const pickCellMinHeight = (slots: WeekSlot[]): string => {
  const hasStackedDay = slots.some(slot => entriesForSlot(slot).length > 1);
  return hasStackedDay ? 'min-h-[72px]' : 'min-h-[54px]';
};
